import classNames from 'classnames';
import { IconLogOut, IconSettings, IconUser } from 'featherico';
import * as React from 'react';
import { CurrentUserDto } from '../../../../../../types/UserDtos';
import { logout } from '../../../../../api/custom/UserLogout';
import * as ROUTES from '../../../../../routes';
import Avatar from '../../../../components/Avatar';
import { Divider, Dropdown, Option, OptionLink } from '../../../../components/Dropdown';
import replacement from '../../../../services/Replacement';
import { goToUrl } from '../../../../services/Router';
import { prependAppPath } from '../../../../services/Url';
import { getLogoutRedirectUrl, isAnyAdmin } from '../../../../services/User';
import styles from './style.m.less';

const NoIcon = () => <span className={styles.noIcon} aria-hidden />;

type Props = {
    user: CurrentUserDto;
};

const UserMenu: React.FC<Props> = ({ user }) => {
    return (
        <Dropdown
            kind="PLAIN"
            showChevron={false}
            ariaLabel={`You're logged in as ${user.firstName}. Click to open user settings menu.`}
            label={<UserButton user={user} />}
            eventTracking={{ category: 'navigation user menu', action: 'toggle' }}
            large={false}
            className={classNames('top-aligned', 'left-aligned')}
            buttonClass={styles.userDropdownButton}
        >
            {!user.isGuest && (
                <>
                    <OptionLink
                        href={prependAppPath(ROUTES.USER_PROFILE_SHORT)}
                        eventTracking={{ category: 'navigation user menu', action: 'click', label: 'profile' }}
                    >
                        <IconUser className={styles.menuIcon} aria-hidden />
                        <span className={styles.menuItemLabel}>Profile</span>
                    </OptionLink>
                    <OptionLink
                        href={prependAppPath(ROUTES.USER_SETTINGS)}
                        eventTracking={{ category: 'navigation user menu', action: 'click', label: 'your settings' }}
                    >
                        <IconSettings className={styles.menuIcon} aria-hidden />
                        <span className={styles.menuItemLabel}>Settings</span>
                    </OptionLink>
                </>
            )}
            <OptionLink
                href="/backups"
                eventTracking={{ category: 'navigation user menu', action: 'click', label: 'backups' }}
            >
                <NoIcon />
                <span className={styles.menuItemLabel}>Backups</span>
            </OptionLink>
            {!user.isGuest && (
                <OptionLink
                    href="/app/downloads"
                    eventTracking={{ category: 'navigation user menu', action: 'click', label: 'exports' }}
                >
                    <NoIcon />
                    <span className={styles.menuItemLabel}>Exports</span>
                </OptionLink>
            )}
            {!user.isGuest && !isAnyAdmin(user) && (
                <OptionLink
                    href={prependAppPath(ROUTES.PEOPLE_DIRECTORY)}
                    eventTracking={{ category: 'navigation user menu', action: 'click', label: 'company directory' }}
                >
                    <NoIcon />
                    <span className={styles.menuItemLabel}>{replacement('company.big')} Directory</span>
                </OptionLink>
            )}
            <Divider />
            <Option
                onClick={() => {
                    logout().then(() => goToUrl(getLogoutRedirectUrl(user)));
                }}
                eventTracking={{ category: 'navigation user menu', action: 'click', label: 'logout' }}
            >
                <IconLogOut className={styles.menuIcon} aria-hidden />
                <span className={styles.menuItemLabel}>Log Out</span>
            </Option>
            {user.isSmallImprovements && (
                <>
                    <Divider />
                    <OptionLink href="/app/observer/companies">
                        <NoIcon />
                        <span className={styles.menuItemLabel}>Company Observer</span>
                    </OptionLink>
                    <OptionLink href="/app/observer/users">
                        <NoIcon />
                        <span className={styles.menuItemLabel}>User Observer</span>
                    </OptionLink>
                    <OptionLink href="/app/observer/experiments">
                        <NoIcon />
                        <span className={styles.menuItemLabel}>Experiment Observer</span>
                    </OptionLink>
                    <OptionLink href="/app/observer/users-to-emails">
                        <NoIcon />
                        <span className={styles.menuItemLabel}>Users to Emails Tool</span>
                    </OptionLink>
                    <OptionLink href="/app/observer/features">
                        <NoIcon />
                        <span className={styles.menuItemLabel}>Bulk Changes</span>
                    </OptionLink>
                    <OptionLink href="/app/observer/customer-health">
                        <NoIcon />
                        <span className={styles.menuItemLabel}>Customer Health</span>
                    </OptionLink>
                    <OptionLink href="/app/system-access-roles">
                        <NoIcon />
                        <span className={styles.menuItemLabel}>System Access Roles</span>
                    </OptionLink>
                    <OptionLink href="/paymentObserver">
                        <NoIcon />
                        <span className={styles.menuItemLabel}>Payment Observer</span>
                    </OptionLink>
                </>
            )}
        </Dropdown>
    );
};

function UserButton({ user }: { user: CurrentUserDto }) {
    return (
        <>
            <Avatar user={user} size="MD" />
            <div className={styles.userDropdownButtonLabel}>
                {user.name} {user.impersonator && <>[{user.impersonator.firstName}]</>}
            </div>
        </>
    );
}

export default UserMenu;
